import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

interface AppSidebarProps {
  sidebarCollapsed: boolean;
  lecturerDisplayName: string;
  onToggleSidebar: () => void;
  onLogout: () => void;
  mobileMenuOpen?: boolean;
  onCloseMobileMenu?: () => void;
}

function AppSidebar({
  sidebarCollapsed,
  lecturerDisplayName,
  onToggleSidebar,
  onLogout,
  mobileMenuOpen = false,
  onCloseMobileMenu,
}: AppSidebarProps) {
  const location = useLocation();
  const [dashboardExpanded, setDashboardExpanded] = useState(location.pathname.startsWith('/dashboard'));

  useEffect(() => {
    if (location.pathname.startsWith('/dashboard')) {
      setDashboardExpanded(true);
    }
  }, [location.pathname]);

  return (
    <>
      {/* Desktop edge toggle – chỉ hiện trên desktop */}
      <button
        type="button"
        className="sidebar-edge-toggle no-print"
        onClick={onToggleSidebar}
        aria-expanded={!sidebarCollapsed}
        aria-label={sidebarCollapsed ? 'Hiện sidebar' : 'Ẩn sidebar'}
        title={sidebarCollapsed ? 'Hiện sidebar' : 'Ẩn sidebar'}
      >
        <span
          className={`sidebar-edge-toggle-icon ${sidebarCollapsed ? 'is-collapsed' : ''}`}
          aria-hidden="true"
        />
      </button>

      <aside className={`app-sidebar no-print ${mobileMenuOpen ? 'is-mobile-open' : ''}`}>
        {/* Close button – chỉ hiện trên mobile */}
        <button
          type="button"
          className="sidebar-mobile-close"
          onClick={onCloseMobileMenu}
          aria-label="Đóng menu"
        >
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>

        <div className="brand-block">
          <div className="brand-mark">S</div>
          <div>
            <strong>Sổ ảnh</strong>
            <span>Thi sinh dự thi</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <NavLink
            to="/classes"
            className={({ isActive }) => `sidebar-link ${isActive ? 'is-active' : ''}`}
          >
            <span>Sổ ảnh</span>
            <small>Quản lý danh sách ảnh</small>
          </NavLink>
          <NavLink
            to="/import-history"
            className={({ isActive }) => `sidebar-link ${isActive ? 'is-active' : ''}`}
          >
            <span>Lịch sử import</span>
            <small>Theo dõi các lần nhập file</small>
          </NavLink>

          <NavLink
            to="/share"
            className={({ isActive }) => `sidebar-link ${isActive ? 'is-active' : ''}`}
          >
            <span>Chia sẻ</span>
            <small>Quản lý link chia sẻ theo từng lớp</small>
          </NavLink>

          <div className="sidebar-group">
            <div 
              className="sidebar-group-title"
              onClick={() => setDashboardExpanded(!dashboardExpanded)}
              role="button"
              tabIndex={0}
            >
              <div className="sidebar-group-title-text">
                <span>Dashboard</span>
                <small>Tổng hợp nhanh theo lớp phụ trách</small>
              </div>
              <i className={`bi bi-chevron-${dashboardExpanded ? 'down' : 'right'}`} aria-hidden="true"></i>
            </div>
            {dashboardExpanded && (
              <div className="sidebar-sub-nav">
                <NavLink
                  to="/dashboard/upcoming"
                  className={({ isActive }) => `sidebar-link sidebar-sublink ${isActive ? 'is-active' : ''}`}
                >
                  <span>Lịch sắp tới</span>
                </NavLink>
                <NavLink
                  to="/dashboard/gantt"
                  className={({ isActive }) => `sidebar-link sidebar-sublink ${isActive ? 'is-active' : ''}`}
                >
                  <span>Sơ đồ Gantt sử dụng phòng</span>
                </NavLink>
                <NavLink
                  to="/dashboard/monitoring"
                  className={({ isActive }) => `sidebar-link sidebar-sublink ${isActive ? 'is-active' : ''}`}
                >
                  <span>Giám sát & Điều phối kỳ thi</span>
                </NavLink>
              </div>
            )}
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-avatar" aria-hidden="true">GV</div>
          <div className="sidebar-user-meta">
            <strong>{lecturerDisplayName}</strong>
            <span>Giảng viên</span>
          </div>
          <button type="button" className="sidebar-logout" onClick={onLogout}>Đăng xuất</button>
        </div>
      </aside>
    </>
  );
}

export default AppSidebar;
